import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { SltDataGenerationService } from '../../services/slt-data-generation.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-slt-data-generation',
    templateUrl: './slt-data-generation.component.html',
    styleUrls: ['./slt-data-generation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltDataGenerationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Standard component properties
  modalTitle = 'Data Generation Dates/Error';
  minimise = false;
  loading = false;
  noRecords = false;

  // Component-specific properties
  sltDataGenerationData: any[] = [];
  clientKey: any;

  // Global settings
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private sltDataGenerationService: SltDataGenerationService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    this.clientKey =
      (header as any).client_key ??
      header.globalParams?.client?.client_key ??
      header.globalParams?.client_key ??
      header.userSettingsData?.user?.client_key;
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Verify client_key is available
        if (!this.clientKey) {
          console.error('SltDataGeneration: client_key is undefined. Check headerData.');
          this._snackBar.open('Client key not available. Please try again.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }

        // Load SLT data generation information
        this.getSltDataGeneration();
      }
    } catch (e) {
      console.error('Error in SltDataGeneration init:', e);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Get SLT Data Generation data from API
   */
  getSltDataGeneration() {
    this.loading = true;
    this.noRecords = false;
    this.cdr.detectChanges();

    this.sltDataGenerationService.getSltDataGenerationData(
      this.GENERAL_CONFIG.base_url,
      this.clientKey,
      this.rowData.SUBMISSION_ID,
      this.rowData.LE_KEY,
      this.rowData.MEF_GROUP_KEY
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response.callSuccess === "1") {
          this.sltDataGenerationData = response.jsonObject || [];

          // Check if there are no records
          if (this.sltDataGenerationData.length === 0) {
            this.noRecords = true;
          }

          this.loading = false;
          this.cdr.detectChanges();
        } else {
          this.loading = false;
          this.noRecords = true;

          // Show error message if available
          if (response.data && response.data.message) {
            this._snackBar.open(response.data.message, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }

          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        this.loading = false;
        this.noRecords = true;
        console.error('Error loading SLT data generation data:', error);

        this._snackBar.open('Error loading data generation information. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });

        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Cancel and close modal
   */
  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
